/**
 * **A chart card's four states, once.** Pending, failed, empty, drawn — with the plot's box reserved
 * across every one of them.
 *
 * ── Why this is shared rather than written twice ─────────────────────────────────────────────
 * Trendy's three cards and the Ankiety tab's strip had the same eighty lines: a head that keeps its
 * figure's line box, a skeleton gate over a reserved plot, an `EmptyState` in that same box when the
 * read comes back with nothing, the chart under a reveal class, and a caption that is true before
 * the data arrives and therefore stands in every state. The two copies were already one pixel apart
 * in places, and the reserve rule is the one this app measures: a second copy of it is a second
 * place for a card to start jumping.
 *
 * ── What it does NOT own ─────────────────────────────────────────────────────────────────────
 * The head, the caption, the failure sentence, the card's own class and the plot's height are the
 * caller's, because they are what actually differs between a tonnage card and a trainer's own
 * question. The height arrives as a token string — the same idiom `ReadoutChart` already uses for
 * its own `height` — so one reserve class serves both surfaces instead of two near-identical ones.
 *
 * ── The gate is the CALLER's, and that is why `children` is a plain node ─────────────────────
 * 21 September 2026. The wait branch a surface draws has to be forked on the gate rather than on
 * the read, so the gate now belongs to whoever owns the read — a card cannot hold its bars for
 * their minimum life if the only thing that knows about that life is inside the card. With the gate
 * arriving as a prop, a render prop would hand the caller back `gate.reveal`, a value it is already
 * holding; so `children` is a `ReactNode` and each caller puts the class on its own chart.
 *
 * ── `aria-busy` travels with the BARS ────────────────────────────────────────────────────────
 * Owner, 21 September 2026. The shell used to take a `pending` prop whose only job was to report
 * the read on the wait reserve, on the reasoning that the gate's 300ms tail was a courtesy to the
 * eye. Under the gate that reasoning inverts: for the whole tail the reserve still holds
 * placeholder bars and is still about to change, so `aria-busy="false"` there claims a settlement
 * that has not happened. It keys on `gate.waiting`, beside the branch and the hidden sentence, and
 * the prop that carried the read is gone.
 *
 * The props take their defaults in the BODY. A destructured default makes
 * `babel-plugin-react-compiler@1.0.0` skip the whole component, silently — see `ReadoutChart`.
 */

import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';

import { EmptyState } from '../EmptyState';
import { TrendMarkIcon } from '../EmptyStateIcons';
import skel from '../skeleton.module.css';
import hidden from '../visuallyHidden.module.css';
import styles from './ChartCardShell.module.css';

export interface ChartCardShellProps {
  /** The card itself — the caller's own card class, and any modifier it carries. */
  readonly className?: string;
  /** The plot's reserved height, as a token: `var(--h-chart-plot)`. */
  readonly height: string;
  /** The eyebrow, the figure and the change beside it. The caller's, because it is the answer. */
  readonly head: ReactNode;
  /**
   * What the chart COUNTS. True before the data lands, so it stands in every state but a failure.
   *
   * Optional, and the Ankiety strip passes none: owner, 16 September 2026 — a sentence under every
   * card of a six-card row is six copies of the same advice about how to read a chart, and the
   * chart says it. Trendy keeps its captions, which count something the plot does not show.
   */
  readonly caption?: ReactNode;
  /** A control that belongs under the plot rather than in the head — Trendy's scale switch. */
  readonly below?: ReactNode;
  /**
   * The sentence for a failed read, in the caller's own voice — drawn as an `EmptyState` inside the
   * plot's own reserve, exactly like the empty one.
   *
   * A failure used to be the one state that DID collapse: the caller handed in a bare notice and
   * the shell drew it in place of the plot and dropped the caption with it, so a card that came
   * back 500 was a plot and a line shorter than the one beside it and everything below travelled.
   * That is the jump the whole loading policy is written against, and a card cannot hold its box in
   * three states out of four.
   *
   * `null` keeps the box and draws NOTHING in it — the one case being a card whose own surface has
   * already said the sentence somewhere better. A drawing with no words under it reads as a card
   * that failed to load its own notice.
   */
  readonly failedTitle: ReactNode;
  /**
   * „Brak pomiarów w tym zakresie" — a statement about the RANGE, so the tone is neutral. `null` is
   * a silent reserve, as on `failedTitle`.
   */
  readonly emptyTitle: ReactNode;
  /**
   * The caller's wait, from `shared/lib/useSkeletonGate` over its own read. `gate.waiting` picks the
   * wait branch, `gate.skeleton` the bars inside it, and `gate.reveal` is the caller's to put on
   * whatever it renders as `children`.
   */
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  /**
   * The read answered and there is nothing to draw. Separate from the wait, which is also true
   * while a series is empty — the conflation is what the loading policy exists to prevent.
   */
  readonly empty: boolean;
  /** The chart. The caller applies `gate.reveal` to it, because the caller owns the gate. */
  readonly children: ReactNode;
}

export function ChartCardShell(props: ChartCardShellProps) {
  const { t } = useTranslation();
  const reserveStyle = { '--chart-card-reserve': props.height } as CSSProperties;

  return (
    <section className={props.className}>
      {props.head}

      {/* The WAIT is the outermost branch, and the failure lives inside the not-waiting arm. A
        * failure tested first drew its sentence while `aria-busy` still said the region was
        * changing, because the attribute follows `gate.waiting` and a retry is a read like any
        * other: the bars belong to it too, and „nie udało się" is the answer that replaces them. */}
      {props.gate.waiting ? (
        <div className={styles.reserve} style={reserveStyle} aria-busy="true">
          <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
          {/* A plot's shape is a line. One bar across the box the chart will occupy, inside the
            * SAME reserve, so drawing it moves nothing. */}
          {props.gate.skeleton ? (
            <i className={`${String(skel.bar)} ${String(styles.skelPlot)}`} />
          ) : null}
        </div>
      ) : props.failed ? (
        /* NEUTRAL: the read did not answer. It is a statement about this attempt, and the trainer's
         * move is to reload rather than to go and make something. It takes the hand-over like the
         * chart does: it is what replaced the bar. */
        <div className={styles.reserve} style={reserveStyle}>
          {props.failedTitle === null ? null : (
            <EmptyState
              className={props.gate.reveal}
              mark={<TrendMarkIcon />}
              tone="neutral"
              title={props.failedTitle}
            />
          )}
        </div>
      ) : props.empty ? (
        <div className={styles.reserve} style={reserveStyle}>
          {props.emptyTitle === null ? null : (
            <EmptyState
              className={props.gate.reveal}
              mark={<TrendMarkIcon />}
              tone="neutral"
              title={props.emptyTitle}
            />
          )}
        </div>
      ) : (
        props.children
      )}
      {/* The switch goes with the plot; the caption is true in every state, failure included. */}
      {props.failed ? null : props.below}
      {props.caption}
    </section>
  );
}
